import { useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * Find a stock item by typing (owner, 2026-11-01: "instead of having item picker we
 * can have item search"). A dropdown of 400+ items was unusable on a phone. Matches
 * any word in the name, unit or category; at most MAX rows show at once.
 */
const MAX = 30;

export default function ItemSearch({ items, exclude = [], onPick, placeholder = "Search an item to add…", testId = "item-search" }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const taken = useMemo(() => new Set(exclude.map(Number)), [exclude]);

  const matches = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return [];
    return items
      .filter(item => !taken.has(Number(item.id)))
      .filter(item => {
        const text = `${item.name} ${item.unit || ""} ${item.category || ""}`.toLowerCase();
        return words.every(w => text.includes(w));
      })
      .slice(0, MAX);
  }, [items, query, taken]);

  const pick = item => {
    onPick(item);
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="relative" ref={box} data-testid={testId}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      <Input
        value={query}
        placeholder={placeholder}
        className="h-11 pl-9"
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={e => { if (e.key === "Enter" && matches.length) { e.preventDefault(); pick(matches[0]); } }}
        aria-label="Search stock items"
        data-testid={`${testId}-input`}
      />
      {open && query.trim() && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-md border bg-card shadow-lg" role="listbox">
          {matches.map(item => (
            <button key={item.id} type="button" role="option" aria-selected="false"
                    className="w-full min-h-11 px-3 py-2 text-left text-body hover:bg-muted flex items-baseline gap-2"
                    onMouseDown={e => e.preventDefault()} onClick={() => pick(item)}
                    data-testid={`${testId}-option-${item.id}`}>
              <span className="flex-1 truncate">{item.name}</span>
              <span className="text-micro text-muted-foreground shrink-0">{item.unit}{item.category ? ` · ${item.category}` : ""}</span>
            </button>
          ))}
          {!matches.length && <p className="px-3 py-2 text-meta text-muted-foreground">No stock item matches "{query.trim()}"</p>}
        </div>
      )}
    </div>
  );
}
